import { Link } from 'react-router-dom'
import {
  BarChart3,
  Sparkles,
  BookOpen,
  ArrowRight,
  Database,
  MapPin,
} from 'lucide-react'
import { useConfig } from '../contexts/ConfigContext'

/**
 * LandingPage Component
 *
 * Main entry point showcasing three dashboard variations:
 * 1. Traditional Dashboard - Filter-driven exploration
 * 2. AI Dashboard - Natural language queries
 * 3. Narrative Dashboard - AI-generated data stories
 */
function LandingPage() {
  const { getAppTitle } = useConfig()

  const dashboards = [
    {
      id: 'traditional',
      title: 'Traditional Dashboard',
      icon: <BarChart3 className="w-12 h-12" />,
      emoji: '📊',
      tagline: 'Explore with Filters & Views',
      description:
        'Deep dive into facility data with interactive maps, grids, charts, and comparison views. Perfect for detailed exploration with advanced filtering.',
      features: [
        'Multi-view exploration (Map, Grid, Charts)',
        'Advanced filtering by attributes',
        'Location hierarchy navigation',
        'Export and download datasets',
      ],
      link: '/dashboard',
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50',
      iconColor: 'text-blue-600',
      suitableFor: 'Data analysts, researchers, policy planners',
    },
    {
      id: 'ai',
      title: 'AI Dashboard',
      icon: <Sparkles className="w-12 h-12" />,
      emoji: '✨',
      tagline: 'Ask Natural Questions',
      description:
        'Query your data using natural language. Get instant AI-generated insights, visualizations, and answers without learning complex interfaces.',
      features: [
        'Natural language queries',
        'AI-generated visualizations',
        'Contextual insights and analysis',
        'Cached query examples',
      ],
      link: '/ai-dashboard',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50',
      iconColor: 'text-purple-600',
      suitableFor: 'Non-technical users, executives, quick insights',
    },
    {
      id: 'narrative',
      title: 'Narrative Dashboard',
      icon: <BookOpen className="w-12 h-12" />,
      emoji: '📖',
      tagline: 'Read Data Stories',
      description:
        'Experience data through AI-crafted narratives that combine rich storytelling with interactive visualizations. Perfect for understanding context and implications.',
      features: [
        'AI-generated data narratives',
        'Location-focused storytelling',
        'Strategic recommendations',
        'Rich visualizations with context',
      ],
      link: '/narrative/d-kayunga/healthcare-access',
      gradient: 'from-emerald-500 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50',
      iconColor: 'text-emerald-600',
      suitableFor: 'Decision-makers, report writers, stakeholders',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/img/logo.png" alt="App Logo" className="h-10 w-10" />
              <h1 className="text-2xl font-bold text-gray-900">
                {getAppTitle()}
              </h1>
            </div>
            <Link
              to="/datasets"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Database className="w-4 h-4" />
              <span>Datasets</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 sm:py-24">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
            <MapPin className="w-4 h-4" />
            <span>AI-Powered Data Storytelling for Uganda Districts</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Explore Data Your Way
          </h2>

          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Three powerful approaches to understanding district facility
            data—from traditional analytics to AI-driven narratives. Choose the
            experience that fits your needs.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span>Health Facilities</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Education Institutions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span>Trends Analysis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span>AI-Enhanced Insights</span>
            </div>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {dashboards.map(dashboard => (
            <Link
              key={dashboard.id}
              to={dashboard.link}
              className="group relative bg-white rounded-2xl shadow-lg border-2 border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              {/* Gradient Background on Hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${dashboard.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
              ></div>

              {/* Card Content */}
              <div className="relative p-8">
                {/* Icon */}
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${dashboard.bgGradient} mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <span className="text-4xl">{dashboard.emoji}</span>
                </div>

                {/* Title & Tagline */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 transition-all">
                  {dashboard.title}
                </h3>
                <p
                  className={`text-sm font-semibold ${dashboard.iconColor} mb-4`}
                >
                  {dashboard.tagline}
                </p>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {dashboard.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {dashboard.features.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <span className="text-emerald-500 mt-0.5">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Suitable For */}
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    SUITABLE FOR
                  </p>
                  <p className="text-sm text-gray-700">
                    {dashboard.suitableFor}
                  </p>
                </div>

                {/* CTA */}
                <div className="flex items-center justify-end gap-2 mt-6 text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                  <span>Explore</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Additional Info */}
        <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border border-blue-200 rounded-2xl p-8 max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Sparkles className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                AI-Enhanced Data Storytelling
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Each dashboard demonstrates a different approach to leveraging
                AI for data exploration and storytelling. Whether you prefer
                hands-on filtering, conversational queries, or narrative-driven
                insights, we&apos;ve designed an experience tailored to your
                workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white/50 backdrop-blur-sm py-8">
        <div className="container mx-auto px-4 text-center text-sm text-gray-600">
          <p>
            Powered by AI • Built with React • Data from Uganda Ministry of
            Health & Education
          </p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
